import { useState, useEffect, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Plus, RefreshCw, AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { pillStyle } from '@/components/StatusDot';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';

const RESULTS = ['Passed', 'Failed', 'Re-inspection Required', 'Pending', 'Scheduled'];
const RESULT_COLORS = {
  'Passed': '#16A34A', 'Failed': '#E5441A', 'Re-inspection Required': '#D97706',
  'Pending': '#031333', 'Scheduled': '#1B4A76',
};

function ResultDot({ result }) {
  return (
    <span className="inline-flex items-center" style={pillStyle(RESULT_COLORS[result])}>{result || 'Pending'}</span>
  );
}

const fmtDate = (iso) => {
  if (!iso) return '\u00A0';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '\u00A0' : d.toLocaleDateString();
};

const EMPTY_ADD = {
  inspection_type: '', inspection_date: '', result: 'Pending', inspector_notes: '',
  follow_up_date: '', follow_up_actions: '', project_id: '', notes: '',
};

export function FdnyInspections({ propertyId }) {
  const confirm = useConfirm();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_ADD);
  const [saving, setSaving] = useState(false);

  const [detail, setDetail] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [detailWarning, setDetailWarning] = useState(null);

  const fetchInspections = useCallback(() => {
    return api.get(`/fdny-inspections?property_id=${propertyId}`)
      .then(r => setInspections(Array.isArray(r.data) ? r.data : []))
      .catch(() => setInspections([]));
  }, [propertyId]);

  useEffect(() => {
    setLoading(true);
    fetchInspections().finally(() => setLoading(false));
    api.get('/projects').then(r => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => setProjects([]));
  }, [fetchInspections]);

  const projectOptions = (Array.isArray(projects) ? projects : []).map(p => ({
    value: p.id, label: `${p.project_number}: ${p.name}`,
  }));

  const openAdd = () => { setAddForm(EMPTY_ADD); setAddOpen(true); };

  const createInspection = async () => {
    setSaving(true);
    try {
      await api.post('/fdny-inspections', {
        property_id: propertyId,
        inspection_type: addForm.inspection_type,
        inspection_date: addForm.inspection_date || null,
        result: addForm.result,
        inspector_notes: addForm.inspector_notes,
        follow_up_date: addForm.follow_up_date || null,
        follow_up_actions: addForm.follow_up_actions,
        project_id: addForm.project_id || null,
        notes: addForm.notes,
      });
      toast.success('Inspection added');
      setAddOpen(false);
      setAddForm(EMPTY_ADD);
      fetchInspections();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error adding inspection');
    }
    setSaving(false);
  };

  const refreshInspection = async (id) => {
    setRefreshingId(id);
    try {
      const r = await api.post(`/fdny-inspections/${id}/fetch`);
      setInspections(prev => prev.map(i => (i.id === id ? r.data : i)));
      if (detail && detail.id === id) { setDetail(r.data); setDetailWarning(r.data?.warning || null); }
      if (r.data?.warning) toast.warning(r.data.warning);
      else toast.success('Refreshed from FDNY Open Data');
    } catch { toast.error('Could not refresh inspection'); }
    setRefreshingId(null);
  };

  const openDetail = (i) => { setDetail(i); setDetailWarning(i?.warning || null); };

  const saveDetail = async (patch) => {
    if (!detail) return;
    setDetail(prev => ({ ...prev, ...patch }));
    try {
      const r = await api.put(`/fdny-inspections/${detail.id}`, patch);
      setDetail(r.data);
      setInspections(prev => prev.map(i => (i.id === r.data.id ? r.data : i)));
    } catch { toast.error('Could not save'); fetchInspections(); }
  };

  const deleteInspection = async (id) => {
    const ok = await confirm({ title: 'Delete inspection', description: 'Delete this FDNY inspection? This cannot be undone.', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/fdny-inspections/${id}`);
      toast.success('Inspection deleted');
      setDetail(null);
      fetchInspections();
    } catch { toast.error('Could not delete inspection'); }
  };

  const rows = Array.isArray(inspections) ? inspections : [];

  return (
    <div className="space-y-2" data-testid="property-fdny-section">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground">FDNY Inspections</p>
        <Button size="sm" variant="outline" className="border-border h-7" onClick={openAdd} data-testid="add-fdny-inspection-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Inspection
        </Button>
      </div>

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading inspections...</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="property-fdny-empty">No FDNY inspections recorded for this property.</p>
      ) : (
        <div className="border border-border rounded-md bg-background overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs text-muted-foreground">Type</TableHead>
                <TableHead className="text-xs text-muted-foreground">Date</TableHead>
                <TableHead className="text-xs text-muted-foreground">Result</TableHead>
                <TableHead className="text-xs text-muted-foreground">Follow-up Date</TableHead>
                <TableHead className="text-xs text-muted-foreground text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(i => (
                <TableRow key={i.id} className="border-border hover:bg-secondary/30" data-testid={`fdny-row-${i.id}`}>
                  <TableCell className="text-sm">{i.inspection_type || '\u00A0'}</TableCell>
                  <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{i.inspection_date || '\u00A0'}</TableCell>
                  <TableCell><ResultDot result={i.result} /></TableCell>
                  <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>{i.follow_up_date || '\u00A0'}</TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => openDetail(i)} className="text-sm text-[#1B4A76] hover:underline" data-testid={`fdny-view-${i.id}`}>View</button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Inspection slide-out */}
      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent className="bg-card border-border w-full sm:max-w-md overflow-y-auto" data-testid="fdny-add-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: '#010A21' }}>Add FDNY Inspection</SheetTitle>
            <SheetDescription>Record an FDNY inspection for this property.</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-5">
            <div>
              <Label className="text-xs text-muted-foreground">Inspection Type</Label>
              <Input value={addForm.inspection_type} onChange={e => setAddForm({ ...addForm, inspection_type: e.target.value })} placeholder="e.g. Sprinkler System" className="bg-background border-border mt-1" data-testid="fdny-add-type" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Inspection Date</Label>
              <Input type="date" value={addForm.inspection_date} onChange={e => setAddForm({ ...addForm, inspection_date: e.target.value })} className="bg-background border-border mt-1" data-testid="fdny-add-date" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Result</Label>
              <Select value={addForm.result} onValueChange={v => setAddForm({ ...addForm, result: v })}>
                <SelectTrigger className="bg-background border-border mt-1" data-testid="fdny-add-result"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">{RESULTS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Inspector Notes</Label>
              <Textarea value={addForm.inspector_notes} onChange={e => setAddForm({ ...addForm, inspector_notes: e.target.value })} rows={2} className="bg-background border-border mt-1" data-testid="fdny-add-inspector-notes" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Follow-up Date</Label>
              <Input type="date" value={addForm.follow_up_date} onChange={e => setAddForm({ ...addForm, follow_up_date: e.target.value })} className="bg-background border-border mt-1" data-testid="fdny-add-followup-date" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Follow-up Actions</Label>
              <Textarea value={addForm.follow_up_actions} onChange={e => setAddForm({ ...addForm, follow_up_actions: e.target.value })} rows={2} className="bg-background border-border mt-1" data-testid="fdny-add-followup-actions" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Project (optional)</Label>
              <div className="mt-1">
                <SearchableSelect options={projectOptions} value={addForm.project_id} onChange={v => setAddForm({ ...addForm, project_id: v })} placeholder="Link a project (optional)" searchPlaceholder="Search projects..." emptyText="No projects." testid="fdny-add-project" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <Textarea value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} rows={2} className="bg-background border-border mt-1" data-testid="fdny-add-notes" />
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90" disabled={saving} onClick={createInspection} data-testid="fdny-add-submit">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</> : 'Add Inspection'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Detail slide-out */}
      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <SheetContent className="bg-card border-border w-full sm:max-w-lg overflow-y-auto" data-testid="fdny-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-3 flex-wrap">
                  <SheetTitle style={{ color: '#010A21' }} data-testid="fdny-detail-title">{detail.inspection_type || 'FDNY Inspection'}</SheetTitle>
                  <ResultDot result={detail.result} />
                </div>
                <SheetDescription>FDNY inspection details and live Open Data status.</SheetDescription>
              </SheetHeader>

              <div className="flex items-center gap-2 mt-4">
                <Button size="sm" variant="outline" className="border-border" disabled={refreshingId === detail.id} onClick={() => refreshInspection(detail.id)} data-testid="fdny-detail-refresh">
                  {refreshingId === detail.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                  Refresh from FDNY
                </Button>
                <Button size="sm" variant="outline" className="border-border text-destructive hover:text-destructive" onClick={() => deleteInspection(detail.id)} data-testid="fdny-detail-delete">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              </div>

              <div className="space-y-4 mt-5">
                <div>
                  <Label className="text-xs text-muted-foreground">Inspection Type</Label>
                  <Input value={detail.inspection_type || ''} onChange={e => setDetail({ ...detail, inspection_type: e.target.value })} onBlur={e => saveDetail({ inspection_type: e.target.value })} className="bg-background border-border mt-1" data-testid="fdny-detail-type" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Inspection Date</Label>
                    <Input type="date" value={detail.inspection_date || ''} onChange={e => setDetail({ ...detail, inspection_date: e.target.value })} onBlur={e => saveDetail({ inspection_date: e.target.value || null })} className="bg-background border-border mt-1" data-testid="fdny-detail-date" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Result</Label>
                    <Select value={detail.result || 'Pending'} onValueChange={v => saveDetail({ result: v })}>
                      <SelectTrigger className="bg-background border-border mt-1" data-testid="fdny-detail-result"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-card border-border">{RESULTS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Inspector Notes</Label>
                  <Textarea value={detail.inspector_notes || ''} onChange={e => setDetail({ ...detail, inspector_notes: e.target.value })} onBlur={e => saveDetail({ inspector_notes: e.target.value })} rows={2} className="bg-background border-border mt-1" data-testid="fdny-detail-inspector-notes" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Follow-up Date</Label>
                  <Input type="date" value={detail.follow_up_date || ''} onChange={e => setDetail({ ...detail, follow_up_date: e.target.value })} onBlur={e => saveDetail({ follow_up_date: e.target.value || null })} className="bg-background border-border mt-1" data-testid="fdny-detail-followup-date" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Follow-up Actions</Label>
                  <Textarea value={detail.follow_up_actions || ''} onChange={e => setDetail({ ...detail, follow_up_actions: e.target.value })} onBlur={e => saveDetail({ follow_up_actions: e.target.value })} rows={2} className="bg-background border-border mt-1" data-testid="fdny-detail-followup-actions" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Notes</Label>
                  <Textarea value={detail.notes || ''} onChange={e => setDetail({ ...detail, notes: e.target.value })} onBlur={e => saveDetail({ notes: e.target.value })} rows={2} className="bg-background border-border mt-1" data-testid="fdny-detail-notes" />
                </div>
                <p className="text-xs text-muted-foreground" data-testid="fdny-detail-last-fetched">
                  {detail.last_fetched_at ? `Last fetched: ${fmtDate(detail.last_fetched_at)}` : 'Never fetched from FDNY'}
                </p>
                {detailWarning && (
                  <div className="flex items-start gap-2 rounded-md px-3 py-2" style={{ backgroundColor: '#FEF3C7', color: '#B45309' }} data-testid="fdny-detail-warning">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <p className="text-xs">{detailWarning}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default FdnyInspections;
